import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import { EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { HERO_FONT_CLASS, HeroFont } from '../ui/HeroFont';

/**
 * Full-bleed Lifestyle hero. Larger & more immersive than the shared PageHero —
 * it sets the mood for the Lifestyle narrative that follows (an editorial
 * cover shot with a short headline + lead-in subtitle).
 */

const bgImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[0].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[0].alt_text',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'A hero-worthy lifestyle image for the Lifestyle page — residents enjoying the community, a staged amenity, or a signature exterior shot. Prefer wide, atmospheric compositions over tight interior detail.',
    context:
      'Full-bleed hero that opens the Lifestyle page. Sets the mood for feature cards that follow. Prefer a different photo from the home hero.',
  },
};

export function LifestyleHero() {
  const bg = useSlotGroup('lifestylehero.bg_image', bgImageDef);

  return (
    <section className="relative w-full h-[60vh] md:h-[75vh] min-h-[420px] max-h-[720px] overflow-hidden">
      <EditableSlotGroup groupPath="lifestylehero.bg_image" definition={bgImageDef}>
        <div className="absolute inset-0">
          {bg.image ? (
            <VibesitesImage
              url={bg.image}
              alt={bg.alt || ''}
              priority
              sizes="100vw"
              className="w-full h-full object-cover"
              style={{
                animation: 'lifestyleHeroKenBurns 35s ease-in-out infinite alternate',
                willChange: 'transform',
                transformOrigin: 'center center',
              }}
            />
          ) : (
            <div className="w-full h-full bg-[var(--vibe-color-text)]" />
          )}
        </div>
      </EditableSlotGroup>

      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-black/15" />

      <div className="relative z-10 flex flex-col items-center justify-end h-full text-center text-white px-6 pb-14 sm:pb-20 md:pb-24">
        <RichTextContent
          path="lifestylehero.eyebrow"
          label="Eyebrow"
          defaultValue='<p>Live Here, Love Here</p>'
          as="p"
          className="font-body text-[11px] sm:text-xs uppercase tracking-[0.35em] text-white/85 mb-4 drop-shadow"
        />
        <RichTextContent
          path="lifestylehero.title"
          label="Title"
          defaultValue='<p>Lifestyles of<br><span data-type="field-token" data-field="property_name"></span></p>'
          as="h1"
          className={`${HERO_FONT_CLASS} text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-normal max-w-4xl leading-[1.05] tracking-tight drop-shadow-lg`}
        />
        <HeroFont />
        <RichTextContent
          path="lifestylehero.subtitle"
          label="Subtitle"
          defaultValue='<p>A community built around comfort, convenience, and the way you actually want to live in <span data-type="field-token" data-field="property_address_city"></span>.</p>'
          as="p"
          className={`${HERO_FONT_CLASS} mt-5 max-w-2xl text-base sm:text-lg md:text-xl text-white/95 leading-relaxed drop-shadow`}
        />
      </div>

      <style>{`
        @keyframes lifestyleHeroKenBurns {
          0%   { transform: scale(1.02) translate(0, 0); }
          100% { transform: scale(1.08) translate(-1%, -1%); }
        }
        @media (prefers-reduced-motion: reduce) {
          [style*="lifestyleHeroKenBurns"] { animation: none !important; transform: none !important; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'LifestyleHero',
  displayName: 'Lifestyle Hero',
  tokens: {
    'lifestylehero.eyebrow': {
      label: 'Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Live Here, Love Here</p>',
    },
    'lifestylehero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue:
        '<p>Lifestyles of<br><span data-type="field-token" data-field="property_name"></span></p>',
    },
    'lifestylehero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue:
        '<p>A community built around comfort, convenience, and the way you actually want to live in <span data-type="field-token" data-field="property_address_city"></span>.</p>',
    },
  },
  slotGroups: {
    'lifestylehero.bg_image': bgImageDef,
  },
};

(LifestyleHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default LifestyleHero;
